import { Box, Container, Typography, Button, Grid, Stack, keyframes } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useKeycloak } from '@react-keycloak/web';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// Floating animation for decorative elements
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-20px) rotate(2deg); }
  66% { transform: translateY(-10px) rotate(-2deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
`;

function HeroSection() {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();

  const handleGetStarted = () => {
    keycloak.register();
  };

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        minHeight: { xs: 'auto', md: '90vh' },
        display: 'flex',
        alignItems: 'center',
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        overflow: 'hidden',
        background: 'transparent',
        pt: { xs: 3, md: 0 },
        pb: { xs: 4, md: 0 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(ellipse 100% 60% at 50% 0%, rgba(144, 202, 249, 0.06), transparent 70%),
            radial-gradient(circle at 85% 15%, rgba(144, 202, 249, 0.04), transparent 40%),
            radial-gradient(circle at 15% 85%, rgba(244, 143, 177, 0.04), transparent 40%)
          `,
          pointerEvents: 'none',
          zIndex: 0,
        }
      }}
    >
      {/* Animated blob decorations - more subtle */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          width: { xs: 200, md: 400 },
          height: { xs: 200, md: 400 },
          borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
          background: 'radial-gradient(circle, rgba(144, 202, 249, 0.08), rgba(144, 202, 249, 0.02))',
          filter: 'blur(80px)',
          animation: `${float} 20s ease-in-out infinite`,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '15%',
          left: '5%',
          width: { xs: 150, md: 300 },
          height: { xs: 150, md: 300 },
          borderRadius: '70% 30% 50% 50% / 60% 40% 60% 40%',
          background: 'radial-gradient(circle, rgba(244, 143, 177, 0.06), rgba(244, 143, 177, 0.01))',
          filter: 'blur(70px)',
          animation: `${float} 25s ease-in-out infinite`,
          animationDelay: '-5s',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 3, sm: 3, md: 3 } }}>
        <Grid container spacing={6} alignItems="center">
          {/* Left Column - Content */}
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                animation: `${pulse} 3s ease-in-out infinite`,
                mb: 3,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <CheckCircleOutlineIcon sx={{ color: '#66bb6a', fontSize: 20 }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#66bb6a',
                    fontWeight: 600,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                  }}
                >
                  {t('landing.hero.trustBadge')}
                </Typography>
              </Stack>
            </Box>

            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                fontWeight: 700,
                lineHeight: 1.1,
                mb: 3,
                background: 'linear-gradient(135deg, #FFFFFF 0%, #90caf9 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {t('landing.hero.title')}
            </Typography>

            <Typography
              variant="h5"
              sx={{
                color: '#ADB5BD',
                mb: 4,
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                lineHeight: 1.6,
                maxWidth: '600px',
              }}
            >
              {t('landing.hero.subtitle')}
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={handleGetStarted}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  py: 1.8,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  borderWidth: 2,
                  borderColor: '#90caf9',
                  color: '#90caf9',
                  background: 'rgba(144, 202, 249, 0.05)',
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#42a5f5',
                    borderWidth: 2,
                    background: 'rgba(144, 202, 249, 0.12)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px rgba(144, 202, 249, 0.3)',
                    '& .MuiButton-endIcon': {
                      transform: 'translateX(4px)',
                    },
                  },
                  '& .MuiButton-endIcon': {
                    transition: 'transform 0.3s ease',
                  },
                }}
              >
                {t('landing.hero.cta')}
              </Button>
            </Stack>

            {/* Trust indicators */}
            <Stack
              direction="row"
              spacing={4}
              sx={{
                mt: 5,
                pt: 4,
                borderTop: '1px solid rgba(144, 202, 249, 0.1)',
              }}
            >
              <Box>
                <Typography variant="h4" sx={{ color: '#90caf9', fontWeight: 700, mb: 0.5 }}>
                  100%
                </Typography>
                <Typography variant="body2" sx={{ color: '#ADB5BD' }}>
                  {t('landing.hero.trustIndicator1')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ color: '#90caf9', fontWeight: 700, mb: 0.5 }}>
                  2
                </Typography>
                <Typography variant="body2" sx={{ color: '#ADB5BD' }}>
                  {t('landing.hero.trustIndicator2')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ color: '#90caf9', fontWeight: 700, mb: 0.5 }}>
                  24/7
                </Typography>
                <Typography variant="body2" sx={{ color: '#ADB5BD' }}>
                  {t('landing.hero.trustIndicator3')}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Right Column - Visual */}
          <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box
              sx={{
                position: 'relative',
                height: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Abstract data visualization */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    width: '80%',
                    height: '80%',
                    borderRadius: '40%',
                    border: '2px solid rgba(144, 202, 249, 0.2)',
                    animation: `${pulse} 4s ease-in-out infinite`,
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    width: '60%',
                    height: '60%',
                    borderRadius: '40%',
                    border: '2px solid rgba(244, 143, 177, 0.2)',
                    animation: `${pulse} 4s ease-in-out infinite`,
                    animationDelay: '-2s',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 200,
                    height: 200,
                    borderRadius: '40%',
                    background: 'linear-gradient(135deg, rgba(144, 202, 249, 0.2), rgba(244, 143, 177, 0.1))',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '4rem',
                  }}
                >
                  📊
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default HeroSection;
